import React, { useCallback, useEffect, useState, useRef } from 'react';
import { DateBox, TextBox, Validator } from 'devextreme-react';
import { RequiredRule } from 'devextreme-react/validator';
import moment from 'moment';

interface BirthDateFieldProps {
  value: string | Date | null;
  onValueChange: (value: Date | null) => void;
  isEditing: boolean;
  label?: string;
  required?: boolean;
}

export const BirthDateField: React.FC<BirthDateFieldProps> = ({ value, onValueChange, isEditing, label = 'Fecha de Nacimiento', required = false }) => {
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [ageText, setAgeText] = useState<string>('');
  const [ageError, setAgeError] = useState<string>('');
  const lastExternalValue = useRef<string | Date | null>(null);
  const isInternalUpdate = useRef<boolean>(false);
  const dateBoxManualInput = useRef<boolean>(false);
  const textBoxManualInput = useRef<boolean>(false);
  const preventAgeCalculation = useRef<boolean>(false);

  // Calcular texto de edad desde fecha (función pura)
  const calculateAgeText = useCallback((date: Date): string => {
    const now = moment();
    const birth = moment(date);

    if (!birth.isValid()) return '';

    const totalDays = now.diff(birth, 'days');

    // Si es mayor o igual a 360 días (aproximadamente 1 año), mostrar en años
    if (totalDays >= 360) {
      const years = now.diff(birth, 'years');
      return `${years} ${years === 1 ? 'Año' : 'Años'}`;
    }

    // Si es mayor o igual a 30 días pero menor a 360 días, mostrar en meses
    if (totalDays >= 30) {
      const months = now.diff(birth, 'months');
      return `${months} ${months === 1 ? 'Mes' : 'Meses'}`;
    }

    // Si es menor a 30 días, mostrar en días
    return `${totalDays} ${totalDays === 1 ? 'Día' : 'Días'}`;
  }, []);

  // Función para comparar si dos fechas son la misma
  const isSameDate = useCallback((date1: string | Date | null, date2: string | Date | null): boolean => {
    if (date1 === null && date2 === null) return true;
    if (date1 === null || date2 === null) return false;

    const moment1 = moment(date1);
    const moment2 = moment(date2);

    if (!moment1.isValid() || !moment2.isValid()) return false;

    return moment1.isSame(moment2, 'day');
  }, []);

  // Sincronizar con el valor externo solo cuando realmente cambie desde fuera
  useEffect(() => {
    // Si es una actualización interna, no hacer nada
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    // Solo actualizar si el valor externo realmente cambió
    if (!isSameDate(value, lastExternalValue.current)) {
      lastExternalValue.current = value;

      if (value) {
        const date = moment(value).isValid() ? moment(value).toDate() : null;
        setBirthDate(date);
        if (date) {
          setAgeText(calculateAgeText(date));
        }
        setAgeError('');
      } else {
        setBirthDate(null);
        setAgeText('');
        setAgeError('');
      }
    }
  }, [value, calculateAgeText, isSameDate]);

  // Calcular fecha desde texto de edad (acepta múltiples formatos)
  const calculateDateFromAge = useCallback((ageStr: string): Date | null => {
    const trimmed = ageStr.trim().toLowerCase();
    if (!trimmed) {
      setAgeError('');
      return null;
    }

    // Patrones para detectar diferentes formatos de entrada
    const patterns = [
      // Formatos con abreviaciones: 5a, 3m, 15d
      { regex: /^(\d+)\s*([amd])$/, groups: ['amount', 'unit'] },

      // Formatos con espacios: 5 a, 3 m, 15 d
      { regex: /^(\d+)\s+([amd])$/, groups: ['amount', 'unit'] },

      // Formatos con palabras completas: 5años, 3meses, 15días
      { regex: /^(\d+)\s*(años?|meses?|días?)$/, groups: ['amount', 'word'] },

      // Formatos con espacios y palabras: 5 años, 3 meses, 15 días
      { regex: /^(\d+)\s+(años?|meses?|días?)$/, groups: ['amount', 'word'] },
    ];

    let amount: number | null = null;
    let unitType: 'years' | 'months' | 'days' | null = null;

    // Intentar hacer match con cada patrón
    for (const pattern of patterns) {
      const match = trimmed.match(pattern.regex);
      if (match) {
        amount = parseInt(match[1], 10);

        if (pattern.groups[1] === 'unit') {
          // Abreviaciones: a, m, d
          const unit = match[2];
          switch (unit) {
            case 'a':
              unitType = 'years';
              break;
            case 'm':
              unitType = 'months';
              break;
            case 'd':
              unitType = 'days';
              break;
          }
        } else if (pattern.groups[1] === 'word') {
          // Palabras completas: años, meses, días
          const word = match[2];
          if (word.startsWith('año')) unitType = 'years';
          else if (word.startsWith('mes')) unitType = 'months';
          else if (word.startsWith('día')) unitType = 'days';
        }
        break;
      }
    }

    // Si no se encontró un patrón válido
    if (amount === null || unitType === null) {
      setAgeError('Formato inválido. Ejemplos: 5a, 3 años, 15m, 2 meses, 30d, 10 días');
      return null;
    }

    if (isNaN(amount) || amount < 0) {
      setAgeError('La edad debe ser un número positivo');
      return null;
    }

    // Validar rangos razonables
    if (unitType === 'years' && amount > 150) {
      setAgeError('Edad muy alta (máximo 150 años)');
      return null;
    }
    if (unitType === 'months' && amount > 1800) {
      // 150 años * 12 meses
      setAgeError('Edad muy alta (máximo 1800 meses)');
      return null;
    }
    if (unitType === 'days' && amount > 54750) {
      // 150 años * 365 días
      setAgeError('Edad muy alta (máximo 54750 días)');
      return null;
    }

    const now = moment();
    const calculatedDate = now.subtract(amount, unitType);

    setAgeError('');
    return calculatedDate.toDate();
  }, []);

  // Manejar cambio en fecha (cuando el usuario edita manualmente el DateBox)
  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      // Marcar que esta es una actualización interna
      isInternalUpdate.current = true;
      // Marcar que el DateBox fue editado manualmente
      dateBoxManualInput.current = true;
      // Prevenir que el TextBox recalcule automáticamente
      preventAgeCalculation.current = true;

      setBirthDate(newDate);
      if (newDate) {
        const calculatedAge = calculateAgeText(newDate);
        setAgeText(calculatedAge);
        setAgeError('');
        // Actualizar la referencia del último valor externo para evitar loops
        lastExternalValue.current = newDate;
        onValueChange(newDate);
      } else {
        setAgeText('');
        setAgeError('');
        lastExternalValue.current = null;
        onValueChange(null);
      }
    },
    [calculateAgeText, onValueChange]
  );

  // Manejar cambio en texto de edad (cuando el usuario edita manualmente el TextBox)
  const handleAgeChange = useCallback(
    (newAgeText: string) => {
      // Solo procesar si hay entrada manual en el TextBox o si no hay restricción
      if (!textBoxManualInput.current && preventAgeCalculation.current) {
        // Si no hay entrada manual y está restringido, solo actualizar el texto sin recalcular
        setAgeText(newAgeText);
        return;
      }

      // Marcar que esta es una actualización interna
      isInternalUpdate.current = true;
      // Marcar que el TextBox fue editado manualmente
      textBoxManualInput.current = true;
      // Desactivar la prevención ya que ahora sí hay entrada manual
      preventAgeCalculation.current = false;

      // Si el usuario está escribiendo, permitir el texto temporal
      if (!newAgeText.trim()) {
        setAgeText('');
        setAgeError('');
        setBirthDate(null);
        lastExternalValue.current = null;
        onValueChange(null);
        return;
      }

      const calculatedDate = calculateDateFromAge(newAgeText);
      if (calculatedDate) {
        // Si la fecha es válida, actualizar todo y formatear el texto al formato completo
        setBirthDate(calculatedDate);
        // Actualizar la referencia del último valor externo para evitar loops
        lastExternalValue.current = calculatedDate;
        onValueChange(calculatedDate);
        // Formatear al formato completo
        const formattedAge = calculateAgeText(calculatedDate);
        setAgeText(formattedAge);
        // Resetear banderas
        dateBoxManualInput.current = false;
      } else {
        // Si la fecha no es válida, mantener el texto como está para que el usuario pueda seguir escribiendo
        setAgeText(newAgeText);
      }
    },
    [calculateDateFromAge, calculateAgeText, onValueChange]
  );

  // Manejar cuando el TextBox recibe foco
  const handleAgeFocus = useCallback(() => {
    // Resetear la bandera de entrada manual cuando el campo recibe foco
    textBoxManualInput.current = false;
  }, []);

  // Manejar cuando el TextBox pierde foco
  const handleAgeBlur = useCallback(() => {
    // Resetear banderas cuando el campo pierde foco
    textBoxManualInput.current = false;
    preventAgeCalculation.current = false;
  }, []);

  // Manejar entrada por teclado en el TextBox
  const handleAgeKeyDown = useCallback(() => {
    // Marcar que hay entrada manual por teclado
    textBoxManualInput.current = true;
  }, []);

  if (!isEditing) {
    // Modo solo lectura
    return (
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <DateBox value={birthDate || undefined} label={label} stylingMode='outlined' labelMode='static' readOnly={true} displayFormat='dd/MM/yyyy' />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <TextBox value={ageText} label='Edad' stylingMode='outlined' labelMode='static' readOnly={true} />
        </div>
      </div>
    );
  }

  // Modo edición
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <DateBox
          value={birthDate || undefined}
          onValueChanged={(e) => handleDateChange(e.value)}
          label={label}
          stylingMode='outlined'
          labelMode='static'
          displayFormat='dd/MM/yyyy'
          max={new Date()} // No permitir fechas futuras
        >
          {required && (
            <Validator>
              <RequiredRule message={`${label} es requerido`} />
            </Validator>
          )}
        </DateBox>
      </div>
      <div style={{ flex: '0 0 120px' }}>
        <TextBox value={ageText} onValueChanged={(e) => handleAgeChange(e.value)} onFocusIn={handleAgeFocus} onFocusOut={handleAgeBlur} onKeyDown={handleAgeKeyDown} label='Edad' stylingMode='outlined' labelMode='static' placeholder='Ej: 5a, 3 años, 15m, 2 meses' maxLength={15} validationError={ageError || null} validationStatus={ageError ? 'invalid' : 'valid'} />
        {!ageError && <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>Ej: 5a, 3 años, 15m, 2 m</div>}
      </div>
    </div>
  );
};
